import type { TplHistory, TplMonth } from "@/modules/fulfilment_costs/api";
import {
  INDEX_FLOOR,
  isBilled,
  withMinimums,
  type Minimums,
  type RunRate,
  type Standardised,
} from "@/modules/sourcing_funnel/pages/tpl_cost_model/tplCostModel";

export { invoiceToGbp } from "@/modules/sourcing_funnel/pages/tpl_cost_model/tplCostModel";
export type { Minimums } from "@/modules/sourcing_funnel/pages/tpl_cost_model/tplCostModel";

export const DRIVER_KEYS = [
  "pickPack",
  "packaging",
  "courier",
  "amendments",
  "amazonInbound",
  "goodsIn",
  "storage",
  "subscription",
  "other",
  "duty",
  "minimumTopUp",
] as const;
export type DriverKey = (typeof DRIVER_KEYS)[number];

export const DRIVER_LABELS: Record<DriverKey, string> = {
  pickPack: "Pick and pack",
  packaging: "Packaging",
  courier: "Courier",
  amendments: "Order amendments",
  amazonInbound: "Amazon inbound",
  goodsIn: "Goods in",
  storage: "Storage",
  subscription: "Subscription",
  other: "Other",
  duty: "Duty and customs",
  minimumTopUp: "Minimum top-up",
};

export type PickPackUnit = "unit" | "customer parcel";

export interface RateCard {
  optionId: string;
  label: string;
  region: string;
  currency: string;
  source: "bills" | "offer";
  pickPackRate?: number | null;
  pickPackUnit?: PickPackUnit;
  packagingPerCustomerParcel?: number | null;
  courierPerCustomerParcel?: number | null;
  amendmentsPerCustomerParcel?: number | null;
  amazonInboundPerParcel?: number | null;
  amazonInboundUnit?: string;
  goodsInMonth: number;
  storageMonth: number;
  storagePerUnit?: number | null;
  subscriptionMonth: number;
  otherPerParcel?: number | null;
  dutyMonth?: number;
  minimums: Minimums;
  unitsPerOrder: number | null;
  thin: boolean;
  /** Parcels the bill rates were read from (chosen months); names the volume behind a low-volume card. */
  parcelsCounted?: number;
  excludedUnsplitMonths?: string[];
  /** Compatibility aliases for older callers; pricing uses the individual driver fields above. */
  perUnitPickPack: number | null;
  perCustomerParcel: number | null;
  perAmazonParcel: number | null;
  perOtherParcel: number | null;
  combined: false;
}

export interface ModelDrivers {
  customerParcels: number;
  amazonParcels: number;
  unitsPerOrder: number;
  storage: number;
  goodsIn: number;
}

export type FixedMode = "own" | "same";

export interface CostResult {
  monthly: number | null;
  yearly: number | null;
  topUp: number;
  partial: string | null;
  blocks: Record<DriverKey, number>;
}

export interface RankedOption extends RateCard, CostResult {
  /** Complete AND not low volume: only these enter the headline and "vs current" (B07). */
  ranked: boolean;
  vsCurrent: number | null;
  vsCurrentPct: number | null;
}

const OTHER_TYPES = new Set(["other_labour", "other", "other_not_itemised", "stock_freight"]);
const hasType = (month: TplMonth, type: string): boolean => month.costs.some((cost) => cost.cost_type === type);
const lineMonths = (history: TplHistory): TplMonth[] => history.months.filter((month) =>
  isBilled(history.provider.provider_id, month)
  && month.parcels.total > 0
  && !hasType(month, "fulfilment_unsplit"));
const amountIn = (months: TplMonth[], wanted: ReadonlySet<string>): number => months.reduce(
  (total, month) => total + month.costs.reduce(
    (monthTotal, cost) => monthTotal + (wanted.has(cost.cost_type) ? Number(cost.amount) : 0),
    0,
  ),
  0,
);
const per = (amount: number, count: number): number | null => count > 0 ? amount / count : null;
const billed = (months: TplMonth[], type: string): boolean => months.some((month) => hasType(month, type));
/** Missing is not zero (B15): a driver with volume but no bill line in the chosen months has no price. */
const lineRate = (months: TplMonth[], type: string, count: number): number | null =>
  billed(months, type) ? per(amountIn(months, new Set([type])), count) : null;

/** One independent rate per PFC bill line driver; unsplit fulfilment months are excluded. */
export function rateCardFromBills(
  _rate: RunRate,
  history: TplHistory,
  minimums: Minimums,
  _splitUnreliable = false,
): RateCard {
  const months = lineMonths(history);
  const customerParcels = months.reduce((sum, month) => sum + Math.max(0, month.parcels.total - month.parcels.fba), 0);
  const amazonParcels = months.reduce((sum, month) => sum + month.parcels.fba, 0);
  const allParcels = months.reduce((sum, month) => sum + month.parcels.total, 0);
  const units = months.reduce((sum, month) => sum + (month.units ?? 0), 0);
  const hasUnits = units > 0 && months.some((month) => month.units != null);
  const packaging = lineRate(months, "packaging", customerParcels);
  const courier = lineRate(months, "courier", customerParcels);
  const amendments = lineRate(months, "amendments", customerParcels);
  const amazonInbound = lineRate(months, "amazon_reshipping", amazonParcels);
  const storage = amountIn(months, new Set(["storage"]));
  const divisorMonths = months.length;
  const pickPackRate = lineRate(months, "pick_pack", hasUnits ? units : customerParcels);
  // Courier and pick and pack together price customer parcels: both missing = unpriced.
  const perCustomerParcel = courier == null && pickPackRate == null
    ? null
    : (packaging ?? 0) + (courier ?? 0) + (amendments ?? 0) + (hasUnits ? 0 : pickPackRate ?? 0);
  const excludedUnsplitMonths = history.months
    .filter((month) => hasType(month, "fulfilment_unsplit"))
    .map((month) => month.month.slice(0, 7));

  return {
    optionId: history.provider.provider_id,
    label: history.provider.label,
    region: history.provider.region,
    currency: history.provider.currency,
    source: "bills",
    pickPackRate,
    pickPackUnit: hasUnits ? "unit" : "customer parcel",
    packagingPerCustomerParcel: packaging,
    courierPerCustomerParcel: courier,
    amendmentsPerCustomerParcel: amendments,
    amazonInboundPerParcel: amazonInbound,
    amazonInboundUnit: history.provider.provider_id === "omnipack" ? "shipment" : "Amazon parcel",
    goodsInMonth: divisorMonths > 0 ? amountIn(months, new Set(["goods_in"])) / divisorMonths : 0,
    storageMonth: divisorMonths > 0 ? storage / divisorMonths : 0,
    storagePerUnit: per(storage, units),
    subscriptionMonth: divisorMonths > 0 ? amountIn(months, new Set(["subscription"])) / divisorMonths : 0,
    otherPerParcel: per(amountIn(months, OTHER_TYPES), allParcels),
    dutyMonth: divisorMonths > 0 ? amountIn(months, new Set(["duty_customs"])) / divisorMonths : 0,
    minimums,
    unitsPerOrder: hasUnits && customerParcels > 0 ? units / customerParcels : null,
    thin: divisorMonths > 0 && allParcels / divisorMonths < INDEX_FLOOR,
    parcelsCounted: allParcels,
    excludedUnsplitMonths,
    perUnitPickPack: hasUnits ? pickPackRate : null,
    perCustomerParcel,
    perAmazonParcel: amazonInbound,
    perOtherParcel: per(amountIn(months, OTHER_TYPES), allParcels),
    combined: false,
  };
}

const missingRate = (card: RateCard, drivers: ModelDrivers): string | null => {
  const legacy = card.pickPackRate === undefined;
  if (drivers.customerParcels > 0 && !legacy && card.pickPackRate == null && card.courierPerCustomerParcel == null) return "customer parcels";
  if (drivers.customerParcels > 0 && !legacy && card.pickPackRate != null && card.pickPackUnit === "unit" && !Number.isFinite(drivers.unitsPerOrder)) return "units per order";
  if (drivers.amazonParcels > 0 && (card.amazonInboundPerParcel ?? card.perAmazonParcel) == null) return "Amazon inbound parcels";
  if (drivers.customerParcels + drivers.amazonParcels > 0 && (card.otherPerParcel ?? card.perOtherParcel) == null) return "other parcels";
  return null;
};

const emptyBlocks = (): Record<DriverKey, number> => Object.fromEntries(DRIVER_KEYS.map((key) => [key, 0])) as Record<DriverKey, number>;

/** Price both bills and offers through the same driver-by-driver monthly calculation. */
export function costAt(card: RateCard, drivers: ModelDrivers, fixedMode: FixedMode): CostResult {
  const partial = missingRate(card, drivers);
  const blocks = emptyBlocks();
  const legacy = card.pickPackRate === undefined;
  const pickPackRate = legacy ? card.perUnitPickPack : card.pickPackRate;
  const pickPackUnit = legacy ? "unit" : card.pickPackUnit;
  blocks.pickPack = (pickPackRate ?? 0) * drivers.customerParcels * (pickPackUnit === "unit" ? drivers.unitsPerOrder : 1);
  blocks.packaging = (legacy ? card.perCustomerParcel ?? 0 : card.packagingPerCustomerParcel ?? 0) * drivers.customerParcels;
  blocks.courier = (legacy ? 0 : card.courierPerCustomerParcel ?? 0) * drivers.customerParcels;
  blocks.amendments = (legacy ? 0 : card.amendmentsPerCustomerParcel ?? 0) * drivers.customerParcels;
  blocks.amazonInbound = (card.amazonInboundPerParcel ?? card.perAmazonParcel ?? 0) * drivers.amazonParcels;
  blocks.goodsIn = fixedMode === "same" ? drivers.goodsIn : card.goodsInMonth;
  blocks.storage = fixedMode === "same" ? drivers.storage : card.storageMonth;
  blocks.subscription = card.subscriptionMonth;
  blocks.other = (card.otherPerParcel ?? card.perOtherParcel ?? 0) * (drivers.customerParcels + drivers.amazonParcels);
  blocks.duty = card.dutyMonth ?? 0;
  const beforeMinimums = DRIVER_KEYS.reduce((sum, key) => sum + blocks[key], 0);
  const parcelCount = drivers.customerParcels + drivers.amazonParcels;
  const row: Standardised = {
    providerId: card.optionId,
    blocks: {
      Fixed: (blocks.goodsIn + blocks.storage + blocks.subscription + blocks.duty) * 12,
      "Customer parcels": (blocks.pickPack + blocks.packaging + blocks.courier + blocks.amendments) * 12,
      "Amazon inbound": blocks.amazonInbound * 12,
      "Other variable": blocks.other * 12,
    },
    total: beforeMinimums * 12,
    perParcel: parcelCount > 0 ? beforeMinimums / parcelCount : null,
    missing: [],
    complete: true,
    combined: false,
  };
  const pickPackPerCustomerParcel = pickPackRate == null ? null : pickPackRate * (pickPackUnit === "unit" ? drivers.unitsPerOrder : 1);
  const withFloors = withMinimums(row, drivers.customerParcels, pickPackPerCustomerParcel, card.minimums);
  blocks.minimumTopUp = withFloors.topUpPerYear / 12;
  return {
    monthly: withFloors.total == null ? null : withFloors.total / 12,
    yearly: withFloors.total,
    topUp: blocks.minimumTopUp,
    partial,
    blocks,
  };
}

const rankTier = (row: { ranked: boolean; partial: string | null }): number => row.ranked ? 0 : row.partial === null ? 1 : 2;

/** Ranked (complete, not low volume) options first, cheapest first; then low-volume, then partial ones, still drawn. */
export function rankOptions(cards: RateCard[], drivers: ModelDrivers, fixedMode: FixedMode, currentId: string): RankedOption[] {
  const costs = cards.map((card) => {
    const cost = costAt(card, drivers, fixedMode);
    return { ...card, ...cost, ranked: cost.partial === null && cost.monthly != null && !card.thin };
  });
  const currentRow = costs.find((row) => row.optionId === currentId);
  const current = currentRow?.ranked ? currentRow.monthly : null;
  return costs.map((row) => ({
    ...row,
    vsCurrent: !row.ranked || row.monthly == null || current == null ? null : row.monthly - current,
    vsCurrentPct: !row.ranked || row.monthly == null || current == null || current === 0 ? null : (row.monthly - current) / current,
  })).sort((a, b) => {
    if (rankTier(a) !== rankTier(b)) return rankTier(a) - rankTier(b);
    if (a.monthly == null) return b.monthly == null ? a.label.localeCompare(b.label) : 1;
    if (b.monthly == null) return -1;
    return a.monthly - b.monthly || a.label.localeCompare(b.label);
  });
}

export interface Offer {
  id: string;
  name: string;
  region: string;
  currency: string;
  pickPackRate: number;
  pickPackUnit: PickPackUnit;
  packaging: number;
  courier: number;
  amendments: number;
  amazonInbound: number;
  amazonInboundUnit: string;
  goodsIn: number;
  storage: number;
  storagePerUnit: number;
  subscription: number;
  other: number;
  duty: number;
  pickPackMinimum: number;
  invoiceMinimum: number;
}

type CompactOffer = { i: string; n: string; r: string; c: string; p: number; pu: PickPackUnit; k: number; q: number; a: number; z: number; zu: string; g: number; s: number; su: number; b: number; o: number; d: number; pm: number; im: number };
const finiteNonNegative = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0;
const validText = (value: unknown, max: number): value is string => typeof value === "string" && value.trim().length > 0 && value.trim().length <= max;

function compactToOffer(value: unknown): Offer | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return null;
  const item = value as Partial<CompactOffer>;
  const numbers = [item.p, item.k, item.q, item.a, item.z, item.g, item.s, item.su, item.b, item.o, item.d, item.pm, item.im];
  if (!validText(item.i, 50) || !validText(item.n, 40) || !validText(item.r, 20) || !/^[A-Z]{3}$/.test(item.c ?? "")
    || (item.pu !== "unit" && item.pu !== "customer parcel") || !validText(item.zu, 30) || !numbers.every(finiteNonNegative)) return null;
  return {
    id: item.i.trim(), name: item.n.trim(), region: item.r.trim(), currency: item.c!, pickPackRate: item.p!, pickPackUnit: item.pu,
    packaging: item.k!, courier: item.q!, amendments: item.a!, amazonInbound: item.z!, amazonInboundUnit: item.zu.trim(),
    goodsIn: item.g!, storage: item.s!, storagePerUnit: item.su!, subscription: item.b!, other: item.o!, duty: item.d!,
    pickPackMinimum: item.pm!, invoiceMinimum: item.im!,
  };
}

export function parseOffers(raw: unknown): Offer[] {
  if (typeof raw !== "string" || raw.trim() === "") return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, 10).flatMap((value) => {
      const offer = compactToOffer(value);
      return offer ? [offer] : [];
    });
  } catch {
    return [];
  }
}

export function serializeOffers(offers: Offer[]): string {
  return JSON.stringify(offers.slice(0, 10).map((offer): CompactOffer => ({
    i: offer.id, n: offer.name, r: offer.region, c: offer.currency, p: offer.pickPackRate, pu: offer.pickPackUnit,
    k: offer.packaging, q: offer.courier, a: offer.amendments, z: offer.amazonInbound, zu: offer.amazonInboundUnit,
    g: offer.goodsIn, s: offer.storage, su: offer.storagePerUnit, b: offer.subscription, o: offer.other, d: offer.duty,
    pm: offer.pickPackMinimum, im: offer.invoiceMinimum,
  })));
}

export function invalidOffers(raw: unknown): boolean {
  if (typeof raw !== "string" || raw.trim() === "") return false;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length > 10) return true;
    return parsed.some((value) => compactToOffer(value) == null);
  } catch {
    return true;
  }
}

export function rateCardFromOffer(offer: Offer): RateCard {
  const customerCombined = offer.packaging + offer.courier + offer.amendments + (offer.pickPackUnit === "customer parcel" ? offer.pickPackRate : 0);
  return {
    optionId: `offer:${offer.id}`, label: offer.name, region: offer.region, currency: offer.currency, source: "offer",
    pickPackRate: offer.pickPackRate, pickPackUnit: offer.pickPackUnit, packagingPerCustomerParcel: offer.packaging,
    courierPerCustomerParcel: offer.courier, amendmentsPerCustomerParcel: offer.amendments,
    amazonInboundPerParcel: offer.amazonInbound, amazonInboundUnit: offer.amazonInboundUnit, goodsInMonth: offer.goodsIn,
    storageMonth: offer.storage, storagePerUnit: offer.storagePerUnit, subscriptionMonth: offer.subscription,
    otherPerParcel: offer.other, dutyMonth: offer.duty, minimums: { pickPack: offer.pickPackMinimum, invoice: offer.invoiceMinimum },
    unitsPerOrder: null, thin: false, excludedUnsplitMonths: [], perUnitPickPack: offer.pickPackUnit === "unit" ? offer.pickPackRate : null,
    perCustomerParcel: customerCombined, perAmazonParcel: offer.amazonInbound, perOtherParcel: offer.other, combined: false,
  };
}
